import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";

type IconSize = "sm" | "md" | "lg" | "xl";

interface IconBoxProps {
  icon: LucideIcon;
  size?: IconSize;
  variant?: "default" | "accent" | "outline" | "filled";
  className?: string;
}

const sizeConfig: Record<IconSize, { container: string; icon: string }> = {
  sm: { container: "w-8 h-8", icon: "w-4 h-4" },
  md: { container: "w-10 h-10", icon: "w-5 h-5" },
  lg: { container: "w-12 h-12", icon: "w-6 h-6" },
  xl: { container: "w-16 h-16", icon: "w-8 h-8" },
};

const variantStyles = {
  default: "bg-accent/10 text-accent",
  accent: "bg-accent text-accent-foreground",
  outline: "bg-transparent border-2 border-border text-foreground",
  filled: "bg-foreground text-background",
};

export const IconBox = ({ icon: Icon, size = "md", variant = "default", className }: IconBoxProps) => {
  const config = sizeConfig[size];
  return (
    <div className={cn("flex items-center justify-center", config.container, variantStyles[variant], className)}>
      <Icon className={config.icon} />
    </div>
  );
};

/* Showcase component for the style guide */
interface IconShowcaseProps {
  icons: { icon: LucideIcon; label: string }[];
}

export const IconShowcase = ({ icons }: IconShowcaseProps) => (
  <div className="space-y-10">
    {/* Size scale */}
    <div>
      <p className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-4">Size Scale</p>
      <div className="flex items-end gap-6">
        {(["sm", "md", "lg", "xl"] as IconSize[]).map((size) => (
          <div key={size} className="text-center space-y-2">
            <IconBox icon={icons[0].icon} size={size} />
            <span className="font-body text-[10px] uppercase tracking-widest text-muted-foreground">{size}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Variants */}
    <div>
      <p className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-4">Variants</p>
      <div className="flex items-center gap-6">
        {(["default", "accent", "outline", "filled"] as const).map((variant) => (
          <div key={variant} className="text-center space-y-2">
            <IconBox icon={icons[0].icon} size="lg" variant={variant} />
            <span className="font-body text-[10px] uppercase tracking-widest text-muted-foreground">{variant}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Icon set */}
    <div>
      <p className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-4">Common Icons</p>
      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-4">
        {icons.map(({ icon, label }) => (
          <div key={label} className="text-center space-y-2">
            <IconBox icon={icon} size="md" />
            <span className="font-body text-[10px] text-muted-foreground block">{label}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default IconBox;
